import React from 'react';
import Modal from '../Modal';
import { Client } from '../../types/weighing';
import { 
  UserIcon, 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

interface ClientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({ isOpen, onClose, client }) => {
  const getTypeDisplay = (type?: string) => {
    if (!type) return 'Non renseigné';
    const typeMap: { [key: string]: string } = {
      'agro-industrie': 'Agro-industrie',
      'commerce-international': 'Commerce international',
      'distribution-locale': 'Distribution locale',
      'export-specialise': 'Export spécialisé',
      'import-export': 'Import-Export',
      'particulier': 'Particulier'
    };
    return typeMap[type] || type;
  };

  const getSecteurDisplay = (secteur?: string) => {
    if (!secteur) return 'Non renseigné';
    const secteurMap: { [key: string]: string } = {
      'cereales': 'Céréales',
      'legumineuses': 'Légumineuses',
      'oleagineux': 'Oléoagineux',
      'elevage': 'Élevage',
      'transformation': 'Transformation alimentaire',
      'distribution': 'Distribution',
      'autre': 'Autre'
    };
    return secteurMap[secteur] || secteur;
  };

  const getStatutDisplay = (statut: string) => {
    return statut === 'actif' ? 'Actif' : 'Inactif';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non renseigné';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails du client" size="lg">
      <div className="space-y-6">
        {/* Informations principales */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <UserIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{client.nom}</h3>
              <p className="text-sm text-gray-500">ID: {client.id}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Type</p>
              <p className="text-sm font-medium text-gray-900">{getTypeDisplay(client.type)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Secteur</p>
              <p className="text-sm font-medium text-gray-900">{getSecteurDisplay(client.secteur)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Statut</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                client.statut === 'actif' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {getStatutDisplay(client.statut)}
              </span>
            </div>
          </div>
        </div>

        {/* Contact et localisation */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <MapPinIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Adresse</p>
              <p className="text-sm font-medium text-gray-900">{client.adresse}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <PhoneIcon className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <p className="text-sm font-medium text-gray-900">{client.telephone}</p>
            </div>
          </div>

          {client.email && (
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{client.email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Informations système */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">Informations système</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Créé le</p>
              <p className="font-medium text-gray-900">{formatDate(client.created_at)}</p>
            </div>
            <div>
              <p className="text-gray-500">Mis à jour le</p>
              <p className="font-medium text-gray-900">{formatDate(client.updated_at)}</p>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ClientDetailsModal;
